import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { listAccounts } from '../accounts/api';
import { listCampaigns } from '../campaigns/api';
import { ApiError } from '../../lib/api';
import { formatMonthYear } from '../../lib/monthYear';
import { createRollout } from './api';

// Converts the HTML `datetime-local` input's value (e.g. "2026-04-21T09:00")
// into an ISO string with timezone info. The browser emits it as local time;
// we store UTC so the server sees a stable instant.
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function RolloutNew() {
  const [params] = useSearchParams();
  const brandFromUrl = params.get('brand') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const brand = (accounts.data ?? []).find((a) => a.id === brandFromUrl && a.accountType === 'brand');

  const campaigns = useQuery({
    queryKey: ['campaigns', 'brand', brandFromUrl],
    queryFn: () => listCampaigns({ brandAccountId: brandFromUrl }),
    enabled: !!brandFromUrl,
  });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [defaultStart, setDefaultStart] = useState('');
  const [defaultEnd, setDefaultEnd] = useState('');
  const [defaultCampaignId, setDefaultCampaignId] = useState<string>('');
  const [notes, setNotes] = useState('');
  // Pre-P2b-3 polish round 5 §2.3: rollout-level monthYear label.
  // Auto-suggest the current calendar month so AMs can submit without
  // typing; they can edit before save (or later via the rollout
  // detail page).
  const [monthYearLabel, setMonthYearLabel] = useState(formatMonthYear(new Date()));
  const [error, setError] = useState<string | null>(null);

  const activeCampaigns = (campaigns.data ?? []).filter((c) => c.status === 'active');

  const mut = useMutation({
    mutationFn: async () => {
      setError(null);
      if (!brand) throw new Error('Missing brand');
      return createRollout({
        brandAccountId: brand.id,
        name,
        slug: slug || undefined,
        defaultStartDate: localToIso(defaultStart),
        defaultEndDate: localToIso(defaultEnd),
        defaultCampaignId: defaultCampaignId || null,
        notes: notes || null,
        monthYearLabel: monthYearLabel.trim(),
      });
    },
    onSuccess: async (rollout) => {
      await qc.invalidateQueries({ queryKey: ['rollouts'] });
      navigate(`/rollouts/${rollout.id}`);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  if (accounts.isLoading) return <CenteredSpinner />;
  if (!brand) {
    return (
      <div className="max-w-xl space-y-3">
        <h1 className="text-2xl font-semibold">New rollout</h1>
        <p className="text-sm text-red-600">
          Missing or invalid <code>?brand=&lt;id&gt;</code> query parameter. Start this flow from a
          brand account's Rollouts section.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">New rollout</h1>
        <p className="text-sm text-slate-600">
          Creating a rollout for <strong>{brand.name}</strong>. Rows generate automatically for every
          active location under this brand.
        </p>
      </div>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) {
            setError('Name is required.');
            return;
          }
          mut.mutate();
        }}
      >
        <Field label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Snap AU — May 2026"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Slug (optional, derived from name)">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="snap-au-may-2026"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Default start date">
            <input
              type="datetime-local"
              value={defaultStart}
              onChange={(e) => setDefaultStart(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Default end date">
            <input
              type="datetime-local"
              value={defaultEnd}
              onChange={(e) => setDefaultEnd(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
        </div>

        {/* Pre-P2b-3 polish round 5 §2.3: rollout-level monthYear
            label. Pre-filled with the current calendar month; AM can
            edit before submit. The {monthYear} templating placeholder
            in Meta naming + Google ad-label templates resolves from
            this value for every row in the rollout. */}
        <Field label="Month-year label">
          <input
            type="text"
            value={monthYearLabel}
            onChange={(e) => setMonthYearLabel(e.target.value)}
            placeholder="May 26"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">
            Substituted into <code className="font-mono">{'{monthYear}'}</code> in
            every Meta naming and Google ad-label template across this rollout.
            Auto-filled with the current month; edit if you're authoring ahead.
          </p>
        </Field>

        <Field label="Default Campaign (optional)">
          <Combobox
            aria-label="Default Campaign"
            searchPlaceholder="Search campaigns…"
            value={defaultCampaignId}
            onValueChange={setDefaultCampaignId}
            disabled={!campaigns.data}
            options={[
              { value: '', label: '— None (rows start unassigned) —' },
              ...activeCampaigns.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <p className="mt-1 text-xs text-slate-500">
            Only active Campaigns for this brand are listed. Leave unset to create rows with no
            assigned Campaign.
          </p>
        </Field>

        <Field label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create rollout'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>
      {children}
    </label>
  );
}
